import moment from 'moment'

const LENSCAP_PLUS = 'LENSCAP_PLUS'
const LENSCAP = 'LENSCAP'
class CartItem {
  constructor(item) {
    this.id = item.id

    this.productUrl = item.productUrl
    this.imageUrl = item.imageUrl
    this.name = item.name

    this.waiver = { ...item.waiver }
    this.waiverable = item.waiverable
    this.quantity = item.quantity
    this.invoice = { ...item.invoice }

    this.productPrice = item.productPrice
    this.availabilityLabel = item.availabilityLabel
    this.discontinued = item.discontinued
    this.availableOn = item.availableOn

    this.correctionMessage = item.correctionMessage || ''
  }

  withQuantityUpdated = newQuantity => {
    const newItem = { ...this, waiver: { ...this.waiver } }
    newItem.quantity = newQuantity
    newItem.waiver.quantity = newQuantity
    return newItem
  }

  getProtection = () => {
    if (this.waiver.type === 'fully_insured' && this.waiver.quantity === this.quantity) {
      return LENSCAP_PLUS
    } else if (this.waiver.type === 'damage_waiver_insured' && this.waiver.quantity === this.quantity) {
      return LENSCAP
    } else {
      return 'none'
    }
  }

  // can this item have this type of protection?
  allowsProtection(protection) {
    switch (protection) {
      case LENSCAP_PLUS:
        return this.waiver.fullyInsuredSinglePrice !== null
      case LENSCAP:
        return this.waiver.singlePrice !== null
      default:
        return true
    }
  }

  withProtectionUpdated = newProtection => {
    const newItem = { ...this, waiver: { ...this.waiver } }

    if (!this.allowsProtection(newProtection)) {
      newProtection = null
    }

    switch (newProtection) {
      case LENSCAP_PLUS:
        newItem.waiver.quantity = newItem.quantity
        newItem.waiver.type = 'fully_insured'
        break
      case LENSCAP:
        newItem.waiver.quantity = newItem.quantity
        newItem.waiver.type = 'damage_waiver_insured'
        break
      default:
        newItem.waiver.quantity = 0
        newItem.waiver.type = 'none'
    }
    // TODO: prices?
    return newItem
  }
}

class RentalDate {
  constructor(date, format = null) {
    if (moment.isMoment(date)) {
      this.momentObj = date
    } else if (format) {
      this.momentObj = moment(date, format)
    } else if (date.match(/([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/)) {
      this.momentObj = moment(date, 'YYYY-MM-DD')
    } else if (date.match(/((0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-[12]\d{3})/)) {
      this.momentObj = moment(date, 'MM-DD-YYYY')
    } else {
      // try to convert whatever to moment obj
      this.momentObj = moment(date)
    }

    if (this.momentObj.isValid()) {
      this.serverDateString = this.momentObj.format('YYYY-MM-DD')
      this.clientDateString = this.momentObj.format('MM/DD/YYYY')
      this.dateText = this.momentObj.format('dddd, MMMM DD, YYYY')
      this.timestamp = this.momentObj.format('X')
    }
    this.holiday = (window._holiday_dates && window._holiday_dates[this.serverDateString]) || null
    this.isWeekend = ((this.momentObj.day() === 6) || (this.momentObj.day() === 0))
    this.isBusinessDay = !this.holiday && !this.isWeekend
  }

  getNextDay = () => {
    return new RentalDate(this.momentObj.add(1, 'day'))
  }

  getPreviousDay = () => {
    return new RentalDate(this.momentObj.subtract(1, 'day'))
  }
}

const getNextBusinessDay = rentalDate => {
  const newRentalDate = rentalDate.getNextDay()
  if (newRentalDate.isBusinessDay) {
    return newRentalDate
  } else {
    // eslint-disable-next-line no-undef
    return getNextBusinessDay(newRentalDate)
  }
}

class RentalPlan {
  constructor(data) {
    this.type = 'rental'

    // item related
    this.cartObject = {}
    data.cart.forEach(item => (this.cartObject[item.id] = new CartItem(item)))
    this.cart = Object.values(this.cartObject)

    // lenscap related
    this.additionalAmountTillFullInsurance = data.additionalAmountTillFullInsurance
    this.canAddFullInsurance = data.canAddFullInsurance
    this.insurancePrice = data.insurancePrice
    this.fullInsuranceAdded = data.fullInsuranceAdded

    // dates
    this.duration = data.duration
    this.arrivalDate = data.arrivalDate
    this.earliestShipDate = data.earliestShipDate
    this.earliestArrivalDate = this.getEarliestArrivalDate(data.earliestArrivalDate, (data.shipType === 'pickup' && data.preferredShippingMethod?.includes('Bay Area')))
    this.returnDate = data.returnDate
    this.earliestShipDateForItems = this.getEarliestShipDateForItems(this.cart)
    this.proposedEarlierArrival = data.proposedEarlierArrival

    // shipping related
    this.shipType = data.shipType || 'delivery'
    this.shipTypes = [
      { name: 'Delivery', value: 'delivery' },
      { name: 'FedEx Office', value: 'fedex_office' },
      { name: 'Pickup', value: 'pickup' }
    ]
    this.zipCode = data.zipCode
    this.shipping = { ...data.shipping }
    this.preferredShippingMethod = data.preferredShippingMethod
    this.shippingMethods = data.shippingMethods

    this.enrollInHd = !!data.enrollInHd
    this.hdEligible = data.hdEligible
    this.hdEnrolled = !!data.hdEnrolled
    this.hdPrice = data.hdPrice

    // prices & checkout
    this.total = data.total
    this.subtotal = data.subtotal
    this.itemSubtotal = data.itemSubtotal
    this.unavailableCartItems = this.getUnavailableCartItems(this.cart)
    this.outOfStockItems = this.getOutOfStockItems(this.cart)
    this.cannotCheckout = !!this.unavailableCartItems.length
    this.invoice = { ...data.invoice }

    // display $0 for all prices if there are no items in the cart
    if (!this.cart.length) {
      this.subtotal = '$0.00'
      this.itemSubtotal = '$0.00'
      this.insurancePrice = '$0.00'
      this.shipping.price = '$0.00'
      this.total = '$0.00'
    }

    // Analytics object
    this.pipetteCartObject = {
      subtotal: data.invoice?.subtotal || 0.00,
      items: data.cart.map((cartItem) => {
        return {
          id: cartItem.id,
          invoice: {
            product_price: cartItem.invoice.productPrice,
            protection_price: cartItem.invoice.protectionPrice,
            subtotal: cartItem.invoice.subtotal
          },
          quantity: cartItem.quantity,
          sale_listing_id: cartItem.saleListing?.id
        }
      })
    }
  }

  getEarliestArrivalDate = (earliestArrivalDate, bayAreaPickup = false) => {
    // Ensure we have an earliestArrivalDate, and that it has a correct value,
    // because we will need to check it against the earliestShipDate to make
    // Bay Area pickup adjustments if neeeded
    earliestArrivalDate ||= getNextBusinessDay(new RentalDate(this.earliestShipDate))
    // Ensure we have a RentalDate object to use for the comparison
    // If it is a string then it will return undefinded, and use the string
    // If it is already a RentalDate object if will return a string to create a new RentalDate object
    earliestArrivalDate = new RentalDate(earliestArrivalDate.serverDateString || earliestArrivalDate)

    // Bay Area pickup requires a two day lead time
    if (bayAreaPickup && earliestArrivalDate.timeStamp < new RentalDate(moment(this.earliestShipDate).add(2, 'days')).timestamp) {
      earliestArrivalDate = getNextBusinessDay(new RentalDate(earliestArrivalDate))
    }

    return earliestArrivalDate
  }

  // items related
  withItemsUpdated = newItemsObject => {
    Object.keys(newItemsObject).forEach((key) => {
      const item = newItemsObject[key]
      const itemQuantity = parseInt(item.quantity)
      let finalQuantity = itemQuantity
      let correctionMessage

      if (itemQuantity < 0) {
        finalQuantity = '1'
        correctionMessage = 'Minimum item quantity is 1.'
      }

      if (itemQuantity > 100) {
        finalQuantity = '100'
        correctionMessage = 'Maximum item quantity is 100.'
      }

      item.quantity = finalQuantity
      item.correctionMessage = correctionMessage

      // remove items with no quantity
      if (newItemsObject[key].quantity === 0) {
        delete newItemsObject[key]
      }
    })

    const newCart = Object.values(newItemsObject)
    return Object.assign(this, {
      cartObject: newItemsObject,
      cart: newCart,
      earliestShipDateForItems: this.getEarliestShipDateForItems(newCart),
      unavailableCartItems: this.getUnavailableCartItems(newCart),
      cannotCheckout: !!this.getUnavailableCartItems(newCart).length
    })
  }

  // date related
  withNewDates = datesObj => {
    const arrivalDate = datesObj.newArrival ? datesObj.newArrival : this.arrivalDate
    const returnDate = datesObj.newReturn ? datesObj.newReturn : this.returnDate
    Object.assign(this, { arrivalDate: arrivalDate, returnDate: returnDate })
    if (this.arrivalDate === this.earliestShipDate) {
      // TODO - this is a quick fix, we need a way to dynamically determine which warehouse is "home base"
      Object.assign(this, { shipType: 'pickup', preferredShippingMethod: 'Local pickup in Memphis, TN' })
    }
    return this
  }

  withProposedNewDates = datesObj => {
    const arrivalDate = datesObj.newArrival ? datesObj.newArrival : this.arrivalDate
    const returnDate = datesObj.newReturn ? datesObj.newReturn : this.returnDate
    return new RentalPlan({
      ...this,
      arrivalDate: arrivalDate,
      returnDate: returnDate
    })
  }

  // checkout related
  getUnavailableCartItems = (cart) => {
    const unrentable = cart.filter(item => {
      if (item.discontinued) {
        return true
      }
      const arrivalDate = new RentalDate(this.arrivalDate)
      const availableOn = new RentalDate(item.availableOn)
      return arrivalDate.momentObj.isBefore(availableOn.momentObj, 'day') || arrivalDate.dateText === availableOn.dateText
    })
    // reverse sort by time
    return unrentable.sort(function (a, b) {
      return new RentalDate(b.availableOn).timestamp - new RentalDate(a.availableOn).timestamp
    })
  }

  getOutOfStockItems = (cart) => {
    const outOfStock = cart.filter(item => !!item.availabilityLabel)

    // reverse sort by time
    return outOfStock.sort(function (a, b) {
      return new RentalDate(b.availableOn).timestamp - new RentalDate(a.availableOn).timestamp
    })
  }

  getEarliestShipDateForItems = (cart) => {
    const unrentableItems = this.getUnavailableCartItems(cart)
    return unrentableItems.length ? unrentableItems[0].availableOn : this.earliestShipDate
  }
}

const correctRentalPlan = rentalPlan => {
  const isDayValid = rentalDate => (
    rentalDate.isBusinessDay &&
    rentalDate.timestamp > earliestShipDate.timestamp &&
    rentalDate.timestamp < latestShipDate.timestamp
  )
  const isReturnDayValid = rentalDate => (
    rentalDate.isBusinessDay &&
      rentalDate.timestamp > earliestShipDate.timestamp
  )

  const getDuration = (start, end) => {
    return end.momentObj.diff(start.momentObj, 'days')
  }

  const getFriendlyDate = rentalDate => {
    if (rentalDate.isWeekend) {
      return rentalDate.momentObj.format('dddd')
    } else if (rentalDate.holiday) {
      return rentalDate.holiday
    } else {
      return rentalDate.clientDateString
    }
  }
  const getNextValidBusinessDay = rentalDate => {
    const newRentalDate = rentalDate.getNextDay()
    if (isDayValid(newRentalDate)) {
      return newRentalDate
    } else {
      return getNextValidBusinessDay(newRentalDate)
    }
  }

  // immediately check validity of dates so function doesn't choke on infinite loop
  const originalArrivalDate = new RentalDate(rentalPlan.arrivalDate)
  let arrivalDate
  if (originalArrivalDate.momentObj.isValid()) {
    // instantiate new RentalDate so we don't mutate original value
    arrivalDate = new RentalDate(rentalPlan.arrivalDate)
  } else {
    arrivalDate = new RentalDate(rentalPlan.earliestShipDate)
  }

  const originalReturnDate = new RentalDate(rentalPlan.returnDate)
  let returnDate
  if (originalReturnDate.momentObj.isValid()) {
    // instantiate new RentalDate so we don't mutate original value
    returnDate = new RentalDate(rentalPlan.returnDate)
  } else {
    returnDate = new RentalDate(rentalPlan.earliestShipDate)
  }

  let dateCorrectionMessage
  let earliestShipDate = new RentalDate(rentalPlan.earliestShipDate)
  const earliestShipDateForItems = new RentalDate(rentalPlan.earliestShipDateForItems)
  const minimumDuration = 1
  const maximumDuration = 90
  const maximumDateDistance = 365
  const latestShipDate = new RentalDate(moment(earliestShipDate.momentObj).add(maximumDateDistance, 'days'))

  let fixedPastDate,
    fixedStockIssue,
    fixedArrivalDate,
    fixedReturnDate,
    fixFarFutureStartDate,
    fixedMinDuration,
    fixedMaxDuration

  // renter chooses arrival in the past
  if (arrivalDate.timestamp <= earliestShipDate.timestamp) {
    arrivalDate = earliestShipDate
    arrivalDate = getNextValidBusinessDay(arrivalDate)

    fixedPastDate = true
  }
  // renter chooses date before all items are available
  if (arrivalDate.timestamp <= earliestShipDateForItems.timestamp) {
    if (earliestShipDateForItems.timestamp > earliestShipDate.timestamp) {
      earliestShipDate = earliestShipDateForItems
    }
    arrivalDate = earliestShipDateForItems
    do {
      arrivalDate = arrivalDate.getNextDay()
    } while (!isDayValid(arrivalDate))

    fixedStockIssue = rentalPlan.unavailableCartItems[0]?.name || null

    if (rentalPlan.unavailableCartItems.length > 0) { fixedStockIssue = 'The ' + fixedStockIssue } else { fixedPastDate = true }
  }

  // if the renter has chosen a date that will not work for a Bay Area pickup
  if (rentalPlan.shipType === 'pickup' && rentalPlan.preferredShippingMethod?.includes('Bay Area')) {
    // Bay Area pickup requires a two day lead time
    const validBayAreaPickupDate = [1, 2].reduce((date, _) => getNextValidBusinessDay(date),
      new RentalDate(earliestShipDate.serverDateString)
    )

    if (arrivalDate.timestamp < validBayAreaPickupDate.timestamp) {
      arrivalDate = validBayAreaPickupDate
      fixedArrivalDate = true
    }
  }

  if (arrivalDate.timestamp > latestShipDate.timestamp) {
    fixFarFutureStartDate = true
  }
  // if the start date is invalid, iterate it backwards until it is valid
  while (!isDayValid(arrivalDate)) {
    arrivalDate = arrivalDate.getPreviousDay()
    if (!fixedArrivalDate) {
      fixedArrivalDate = true
    }
  }

  // renter chooses same day start and end
  while (getDuration(arrivalDate, returnDate) < minimumDuration) {
    returnDate = returnDate.getNextDay()
    fixedMinDuration = true
  }

  // if the duration is too long, iterate end date backwards until it is not
  while (getDuration(arrivalDate, returnDate) > maximumDuration) {
    returnDate = returnDate.getPreviousDay()
    fixedMaxDuration = true
  }

  // if the end date is invalid, iterate forwards until it is valid
  while (!isReturnDayValid(returnDate)) {
    returnDate = returnDate.getNextDay()
    if (!fixedReturnDate) {
      fixedReturnDate = true
    }
  }

  // set user notification messages
  const arrivalDateChangeMessage = `so we moved your arrival date to ${arrivalDate.dateText}`

  if (fixedPastDate) {
    dateCorrectionMessage = 'We can\'t ship from the past, so we changed your arrival date.'
  } else if (fixedStockIssue) {
    dateCorrectionMessage = `${fixedStockIssue} is out of stock, so we moved your arrival date.`
  } else if (fixFarFutureStartDate) {
    dateCorrectionMessage = `We can't take orders more than ${maximumDateDistance} days in the future, ${arrivalDateChangeMessage}.`

    // notify that start date had to be changed from an invalid day and duration was too short
  } else if (fixedArrivalDate && fixedMinDuration) {
    dateCorrectionMessage = `We can't deliver on ${getFriendlyDate(originalArrivalDate)}, ${arrivalDateChangeMessage}, and extended to our ${minimumDuration} day minimum.`

  // notify that end date had to be changed from an invalid day
  } else if (fixedReturnDate && fixedMinDuration) {
    dateCorrectionMessage = `Your rental would end on ${originalArrivalDate.clientDateString}, so we extended the rental to ${getDuration(arrivalDate, returnDate)} days.`

  // double up on fixed start and end date notification
  } else if (fixedArrivalDate && fixedReturnDate) {
    dateCorrectionMessage = `We adjusted your dates because we can't deliver on ${getFriendlyDate(originalArrivalDate)}, and you can't ship back on ${returnDate.clientDateString}.`

    // notify that start date was too far in future
  // notify that start date had to be changed from an invalid day
  } else if (fixedArrivalDate) {
    dateCorrectionMessage = `We can't deliver on ${getFriendlyDate(originalArrivalDate)}, ${arrivalDateChangeMessage}.`

  // notify that an order cannot be zero days
  } else if (fixedMinDuration) {
    dateCorrectionMessage = 'Your rental must be at least a day, so we moved your ship-back date.'

  // notify that order was too long, return date was moved back
  } else if (fixedMaxDuration) {
    dateCorrectionMessage = `Our maximum rental is ${maximumDuration} days, so we moved your ship-back date.`

    // notify that start date / arrival date had to be changed
  } else if (fixedArrivalDate) {
    dateCorrectionMessage = `We can't deliver on ${getFriendlyDate(originalArrivalDate)}, so we moved your arrival date up to ${arrivalDate.clientDateString}.`

  // notify that end date / return date had to be changed
  } else if (fixedReturnDate) {
    dateCorrectionMessage = `You can't send back your rental on ${getFriendlyDate(originalReturnDate)}, so we changed the ship-back date to ${returnDate.clientDateString}.`
  }

  return ({
    rentalPlan: new RentalPlan({
      ...rentalPlan,
      arrivalDate: arrivalDate.serverDateString,
      returnDate: returnDate.serverDateString
    }),
    message: dateCorrectionMessage || null
  })
}

export default RentalPlan
export { CartItem, RentalDate, correctRentalPlan }
